/**
 * Teacher Creation Form Component
 *
 * Reference implementation following UI Standards (see /UI_STANDARDS.md)
 *
 * Standards Applied:
 * - Stack layout with spacing={3} for form fields
 * - Responsive Stack direction={{ xs: 'column', md: 'row' }}
 * - Consistent spacing: mb: 3 for sections, p: 4 for Paper
 * - Breadcrumb navigation for nested pages
 * - Page header with h4 title + body2 description
 * - Success/Error alerts with mb: 3
 * - Client-side validation with real-time error clearing
 * - Loading states on buttons and form fields
 * - FastAPI error handling (string and array formats)
 * - Auto-redirect after successful creation
 * - Proper TypeScript types for all state
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Stack,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TeacherCreate } from '../../types/teacher';

const TeacherForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Dropdown data
  const [schools, setSchools] = useState<any[]>([]);

  const [formData, setFormData] = useState<TeacherCreate>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    employee_id: '',
    department: '',
    hire_date: '',
    school_id: 0,
    is_school_admin: false,
    is_consultant: false,
    github_username: '',
    github_token: '',
    github_repositories: null,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // Load schools on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const schoolsRes = await axios.get('/api/schools/');
        setSchools(schoolsRes.data.results || []);
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load schools');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.first_name || formData.first_name.trim() === '') {
      errors.first_name = 'First name is required';
    }

    if (!formData.last_name || formData.last_name.trim() === '') {
      errors.last_name = 'Last name is required';
    }

    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // employee_id is now optional - will auto-generate if empty

    if (!formData.department || formData.department.trim() === '') {
      errors.department = 'Department is required';
    }

    if (!formData.hire_date || formData.hire_date.trim() === '') {
      errors.hire_date = 'Hire date is required';
    }

    if (!formData.school_id || formData.school_id === 0) {
      errors.school_id = 'School is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? 0 : Number(value)) : value)
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      // Convert empty strings to null for optional fields
      // Rename school_id to school for Django REST Framework
      const payload = {
        ...formData,
        school: formData.school_id,  // Rename school_id -> school
        phone: formData.phone || null,
        github_username: formData.github_username || null,
        github_token: formData.github_token || null,
        github_repositories: formData.github_repositories || null,
      };
      delete (payload as any).school_id;  // Remove school_id from payload

      await axios.post('/api/teachers/', payload);

      setSuccess(true);

      // Redirect to teachers list after 1.5 seconds
      setTimeout(() => {
        navigate('/teachers');
      }, 1500);
    } catch (err: any) {
      console.error('Error creating teacher:', err);
      console.error('Error response data:', err.response?.data);

      if (err.response?.data) {
        const errorData = err.response.data;

        // Handle Django REST Framework validation errors (object with field names)
        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const errorMessages = Object.entries(errorData)
            .map(([field, messages]) => {
              if (Array.isArray(messages)) {
                return `${field}: ${messages.join(', ')}`;
              }
              return `${field}: ${messages}`;
            })
            .join('; ');
          setError(errorMessages || 'Failed to create teacher. Please check your input.');
        }
        // Handle FastAPI format (for backwards compatibility)
        else if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            setError(errorData.detail);
          } else if (Array.isArray(errorData.detail)) {
            const errorMessages = errorData.detail
              .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
              .join(', ');
            setError(errorMessages);
          }
        } else {
          setError('Failed to create teacher. Please try again.');
        }
      } else {
        setError('Failed to create teacher. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/teachers');
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/teachers')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Teachers
        </MuiLink>
        <Typography color="text.primary">New Teacher</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Create New Teacher
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enter the details for the new teacher
        </Typography>
      </Box>

      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Teacher created successfully! Redirecting to teachers list...
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Row 1: First Name and Last Name */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                error={!!validationErrors.first_name}
                helperText={validationErrors.first_name}
                disabled={loading}
              />
              <TextField
                fullWidth
                required
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                error={!!validationErrors.last_name}
                helperText={validationErrors.last_name}
                disabled={loading}
              />
            </Stack>

            {/* Row 2: Email and Phone */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                disabled={loading}
                helperText="Optional"
              />
            </Stack>

            {/* Row 3: Employee ID and Department */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Employee ID"
                name="employee_id"
                value={formData.employee_id || ''}
                onChange={handleChange}
                error={!!validationErrors.employee_id}
                helperText={validationErrors.employee_id || "Optional - Will auto-generate (T001, T002, etc.) if left empty"}
                disabled={loading}
                placeholder="Leave empty to auto-generate"
              />
              <TextField
                fullWidth
                required
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                error={!!validationErrors.department}
                helperText={validationErrors.department}
                disabled={loading}
              />
            </Stack>

            {/* Row 4: School and Hire Date */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth required error={!!validationErrors.school_id}>
                <InputLabel>School</InputLabel>
                <Select
                  name="school_id"
                  value={formData.school_id || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, school_id: Number(e.target.value) }));
                    // Clear validation error for this field
                    if (validationErrors.school_id) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.school_id;
                        return newErrors;
                      });
                    }
                  }}
                  label="School"
                  disabled={loading || loadingData}
                >
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.school_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {validationErrors.school_id}
                  </Typography>
                )}
              </FormControl>
              <TextField
                fullWidth
                required
                type="date"
                label="Hire Date"
                name="hire_date"
                value={formData.hire_date}
                onChange={handleChange}
                error={!!validationErrors.hire_date}
                helperText={validationErrors.hire_date}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            {/* Row 5: School Admin & Consultant Checkboxes */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_school_admin}
                    onChange={handleChange}
                    name="is_school_admin"
                    disabled={loading}
                  />
                }
                label="School Administrator"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.is_consultant}
                    onChange={handleChange}
                    name="is_consultant"
                    disabled={loading}
                  />
                }
                label="Consultant"
              />
            </Box>

            {/* Row 6: GitHub Username and Token */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="GitHub Username"
                name="github_username"
                value={formData.github_username || ''}
                onChange={handleChange}
                disabled={loading}
                helperText="Optional - For GitHub integration"
              />
              <TextField
                fullWidth
                type="password"
                label="GitHub Token"
                name="github_token"
                value={formData.github_token || ''}
                onChange={handleChange}
                disabled={loading}
                helperText="Optional - Personal access token"
              />
            </Stack>

            {/* Action Buttons */}
            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Creating...' : 'Create Teacher'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default TeacherForm;
